import type { Landmark, HandLandmark } from "./posture";

/**
 * Represents a single training sample captured when the user performs the habit.
 */
export interface TrainingSample {
  /** Timestamp when the sample was captured */
  timestamp: number;
  /** Pose landmarks from MediaPipe (body keypoints) */
  poseLandmarks: Landmark[];
  /** Hand landmarks from MediaPipe (can be 0, 1, or 2 hands) */
  handLandmarks: HandLandmark[][];
  /** Video dimensions at time of capture (for normalization) */
  videoWidth: number;
  videoHeight: number;
}

/**
 * Represents a custom habit that the user has created and trained.
 */
export type TrainingKind = "habit" | "neutral";

export interface CustomHabit {
  /** Unique identifier for the habit */
  id: string;
  /** User-friendly name (e.g., "Hair twirling", "Pen clicking") */
  name: string;
  /** Positive samples where the habit is being performed */
  habitSamples: TrainingSample[];
  /** Neutral samples representing normal behavior */
  neutralSamples: TrainingSample[];
  /** Whether this habit should be actively detected */
  enabled: boolean;
  /** When the habit was created */
  createdAt: number;
  /** Last time the habit was modified */
  updatedAt: number;
}

/**
 * Local storage key for persisting habits
 */
export const HABITS_STORAGE_KEY = "slouchy-custom-habits";

/**
 * Manages custom habits in local storage
 */
export class HabitStorage {
  /**
   * Load all custom habits from local storage
   */
  static loadHabits(): CustomHabit[] {
    try {
      const stored = localStorage.getItem(HABITS_STORAGE_KEY);
      if (!stored) return [];
      const rawHabits = JSON.parse(stored) as Array<Record<string, unknown>>;
      return rawHabits.map((raw) => {
        const habitSamples = (raw.habitSamples as TrainingSample[] | undefined) ?? (raw.trainingSamples as TrainingSample[] | undefined) ?? [];
        const neutralSamples = (raw.neutralSamples as TrainingSample[] | undefined) ?? [];
        return {
          id: String(raw.id ?? ""),
          name: String(raw.name ?? "Unnamed habit"),
          habitSamples,
          neutralSamples,
          enabled: Boolean(raw.enabled ?? true),
          createdAt: Number(raw.createdAt ?? Date.now()),
          updatedAt: Number(raw.updatedAt ?? Date.now()),
        } satisfies CustomHabit;
      });
    } catch (error) {
      console.error("Failed to load habits from storage:", error);
      return [];
    }
  }

  /**
   * Save all custom habits to local storage
   */
  static saveHabits(habits: CustomHabit[]): void {
    try {
      localStorage.setItem(HABITS_STORAGE_KEY, JSON.stringify(habits));
    } catch (error) {
      console.error("Failed to save habits to storage:", error);
    }
  }

  /**
   * Create a new habit
   */
  static createHabit(name: string): CustomHabit {
    const now = Date.now();
    return {
      id: `habit-${now}-${Math.random().toString(36).substr(2, 9)}`,
      name,
      habitSamples: [],
      neutralSamples: [],
      enabled: true,
      createdAt: now,
      updatedAt: now,
    };
  }

  /**
   * Add a new habit and save to storage
   */
  static addHabit(name: string): CustomHabit {
    const habits = this.loadHabits();
    const newHabit = this.createHabit(name);
    habits.push(newHabit);
    this.saveHabits(habits);
    return newHabit;
  }

  /**
   * Update an existing habit
   */
  static updateHabit(habitId: string, updates: Partial<Omit<CustomHabit, "id" | "createdAt">>): void {
    const habits = this.loadHabits();
    const index = habits.findIndex((h) => h.id === habitId);
    if (index === -1) return;

    const habit = habits[index];
    if (!habit) return;

    habits[index] = {
      ...habit,
      ...updates,
      id: habit.id,
      createdAt: habit.createdAt,
      updatedAt: Date.now(),
    };
    this.saveHabits(habits);
  }

  /**
   * Delete a habit
   */
  static deleteHabit(habitId: string): void {
    const habits = this.loadHabits();
    const filtered = habits.filter((h) => h.id !== habitId);
    this.saveHabits(filtered);
  }

  /**
   * Toggle habit enabled state
   */
  static toggleHabit(habitId: string): void {
    const habits = this.loadHabits();
    const index = habits.findIndex((h) => h.id === habitId);
    if (index === -1) return;

    const habit = habits[index];
    if (!habit) return;

    habits[index] = {
      ...habit,
      enabled: !habit.enabled,
      updatedAt: Date.now(),
    };
    this.saveHabits(habits);
  }

  /**
   * Add training samples to a habit
   */
  static addTrainingSamples(habitId: string, samples: TrainingSample[], kind: TrainingKind): void {
    const habits = this.loadHabits();
    const index = habits.findIndex((h) => h.id === habitId);
    if (index === -1) return;

    const habit = habits[index];
    if (!habit) return;

    habits[index] = {
      ...habit,
      habitSamples:
        kind === "habit" ? [...habit.habitSamples, ...samples] : habit.habitSamples,
      neutralSamples:
        kind === "neutral" ? [...habit.neutralSamples, ...samples] : habit.neutralSamples,
      updatedAt: Date.now(),
    };
    this.saveHabits(habits);
  }

  /**
   * Clear all training samples for a habit
   */
  static clearTrainingSamples(habitId: string, kind: TrainingKind | "all" = "all"): void {
    const habits = this.loadHabits();
    const index = habits.findIndex((h) => h.id === habitId);
    if (index === -1) return;

    const habit = habits[index];
    if (!habit) return;

    habits[index] = {
      ...habit,
      habitSamples: kind === "habit" || kind === "all" ? [] : habit.habitSamples,
      neutralSamples: kind === "neutral" || kind === "all" ? [] : habit.neutralSamples,
      updatedAt: Date.now(),
    };
    this.saveHabits(habits);
  }

  /**
   * Get a specific habit by ID
   */
  static getHabit(habitId: string): CustomHabit | null {
    const habits = this.loadHabits();
    return habits.find((h) => h.id === habitId) ?? null;
  }

  /**
   * Get all enabled habits
   */
  static getEnabledHabits(): CustomHabit[] {
    return this.loadHabits().filter((h) => h.enabled);
  }
}
